import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal, MaskedLines, Eyebrow } from "../components/site/Reveal";
import { ClientMarquee } from "../components/site/Marquee";
import { CTABand } from "../components/site/CTABand";
import { SERVICES, MINI_STEPS, TICKER, PILLARS, PROCESS_STEPS } from "../lib/content";

const HeroImage = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, -50]);

  return (
    <motion.div style={{ y }} className="relative" data-testid="hero-image-wrap">
      <div className="absolute -inset-6 rounded-3xl bg-amber-400/20 blur-2xl" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-blue-500/10 overflow-hidden"
      >
        <img
          src="/hero-team.jpg"
          alt="BMP Tech One team collaborating in a Sydney office"
          className="w-full h-full object-cover"
          data-testid="hero-image"
        />
      </motion.div>
    </motion.div>
  );
};

export default function Home() {
  return (
    <>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg" aria-hidden="true" />
        <div className="absolute -top-40 -right-40 h-[32rem] w-[32rem] rounded-full bg-blue-600/10 blur-[120px]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20 grid lg:grid-cols-[1.25fr_1fr] gap-16 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs uppercase tracking-[0.22em] text-blue-700"
              data-testid="hero-eyebrow"
            >
              Specialist ICT recruitment · Sydney
            </motion.p>
            <h1 className="mt-7 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0A1F44] leading-[1.05]" data-testid="hero-headline">
              <MaskedLines lines={["The right people.", "The right skills."]} delay={0.1} />
              <MaskedLines lines={["Your next hire."]} delay={0.35} lineClass="text-blue-700" />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-7 text-slate-600 text-base md:text-lg leading-relaxed max-w-xl"
              data-testid="hero-subtext"
            >
              Permanent recruitment, contract staffing and contingent workforce solutions. We connect
              organisations with specialist talent and support placements from the initial brief
              through onboarding and ongoing engagement.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#0A1F44] font-semibold px-7 py-3.5 transition-transform duration-200 hover:scale-[1.04] active:scale-95"
              >
                Discuss your hiring needs
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-[#0A1F44] font-medium px-7 py-3.5 transition-colors duration-200 hover:border-blue-600/60 hover:text-blue-700"
              >
                Explore recruitment services
              </Link>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.95 }}
              className="mt-9 font-mono text-[0.68rem] uppercase tracking-[0.2em] text-slate-600"
            >
              Australian-owned · Sydney-based · 10+ years supporting Australian organisations
            </motion.p>
          </div>
          <HeroImage />
        </div>
      </section>

      <div className="border-y border-white/10 bg-[#0A1F44] overflow-hidden marquee-mask" data-testid="ticker-strip">
        <div className="flex w-max items-center gap-10 py-4 animate-marquee" style={{ animationDuration: "30s" }}>
          {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="flex items-center gap-10 font-mono text-xs uppercase tracking-[0.25em] text-slate-300 whitespace-nowrap">
              {t} <span className="text-amber-400">◆</span>
            </span>
          ))}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-3 gap-10">
          {MINI_STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.12} data-testid={`mini-step-${s.num}`}>
              <p className="font-mono text-sm text-blue-700">{s.num}</p>
              <h3 className="mt-4 font-display text-xl font-bold text-[#0A1F44] tracking-tight">{s.title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed text-sm">{s.desc}</p>
              <div className="mt-6 h-px bg-gradient-to-r from-amber-400/70 to-transparent" />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#F4F7FD] border-y border-slate-200 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Eyebrow testId="home-services-eyebrow">Recruitment services</Eyebrow>
            <h2 className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0A1F44]">
              People for your priorities. Flexibility for your workforce.
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 3) * 0.1}>
                <Link
                  to="/services"
                  data-testid={`home-service-card-${s.id}`}
                  className="group block h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:border-blue-600/50 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/10"
                >
                  <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-blue-700">{s.eyebrow}</p>
                  <h3 className="mt-3 font-display text-lg font-bold text-[#0A1F44] tracking-tight">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-blue-700">
                    {s.cta}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-14 items-start">
          <Reveal>
            <Eyebrow testId="home-approach-eyebrow">A recruitment partner for your workforce</Eyebrow>
            <h2 className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0A1F44]">
              Permanent talent. Contract expertise. Ongoing support.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              For over 10 years, BMP Tech One Pty Ltd has supported organisations across retail,
              government, not-for-profit, utilities, banking & insurance and education with
              specialist recruitment and contingent workforce placements, backed by local account
              management from our Sydney headquarters.
            </p>
            <Link to="/approach" data-testid="home-approach-link" className="group mt-8 inline-flex items-center gap-2 text-blue-700 font-medium">
              Our approach <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="space-y-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1} data-testid={`home-pillar-${i}`}>
                <div className="rounded-2xl border border-slate-200 bg-white p-6 flex gap-5 transition-colors duration-300 hover:border-blue-600/40">
                  <span className="font-mono text-sm text-blue-700 pt-0.5">0{i + 1}</span>
                  <div>
                    <h3 className="font-display font-bold text-[#0A1F44]">{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10">
        <Reveal className="max-w-7xl mx-auto px-6 lg:px-10 mb-8">
          <Eyebrow testId="home-experience-eyebrow">Selected engagement experience</Eyebrow>
        </Reveal>
        <ClientMarquee />
        <p className="max-w-7xl mx-auto px-6 lg:px-10 mt-6 text-xs text-slate-600">
          Selected recruitment and placement experience; no endorsement by the organisations listed is implied.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal className="max-w-2xl">
          <Eyebrow testId="home-process-eyebrow">How we work</Eyebrow>
          <h2 className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-[#0A1F44]">
            A clear process. A supported placement.
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROCESS_STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.1} data-testid={`home-process-step-${s.num}`}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-colors duration-300 hover:border-blue-600/40">
                <p className="font-mono text-2xl font-semibold text-blue-700/60">{s.num}</p>
                <h3 className="mt-4 font-display font-bold text-[#0A1F44]">{s.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABand />
    </>
  );
}
